import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  getAllDocumentationPosts, 
  deleteDocumentationPost,
  updateDocumentationOrder
} from '../../api/documentation';
import DocumentationPostDialog from './DocumentationPostDialog';

const DocumentationCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const DocumentationManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderChanged, setOrderChanged] = useState(false);
  
  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await getAllDocumentationPosts();
      // Sort by order_index
      const sortedData = [...data].sort((a, b) => a.order_index - b.order_index);
      setPosts(sortedData);
      setOrderChanged(false);
    } catch (err) {
      console.error('Error fetching documentation posts:', err);
      setError('Failed to load documentation posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = () => {
    setSelectedPost(null);
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleDeletePost = (post) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePost = async () => {
    if (!selectedPost) return;
    
    try {
      await deleteDocumentationPost(selectedPost.id);
      setPosts(posts.filter(post => post.id !== selectedPost.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting documentation post:', err);
      setError('Failed to delete documentation post');
    }
  };

  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) {
      fetchPosts();
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    
    const newPosts = [...posts];
    const temp = { ...newPosts[index] };
    
    // Swap order_index values
    const tempOrderIndex = temp.order_index;
    temp.order_index = newPosts[index - 1].order_index;
    newPosts[index - 1].order_index = tempOrderIndex;
    
    // Swap positions in array
    newPosts[index] = newPosts[index - 1];
    newPosts[index - 1] = temp;
    
    setPosts(newPosts);
    setOrderChanged(true);
  };

  const handleMoveDown = (index) => {
    if (index === posts.length - 1) return;
    
    const newPosts = [...posts];
    const temp = { ...newPosts[index] };
    
    // Swap order_index values
    const tempOrderIndex = temp.order_index;
    temp.order_index = newPosts[index + 1].order_index;
    newPosts[index + 1].order_index = tempOrderIndex;
    
    // Swap positions in array
    newPosts[index] = newPosts[index + 1];
    newPosts[index + 1] = temp;
    
    setPosts(newPosts);
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    try {
      const orderData = posts.map((post, index) => ({
        id: post.id,
        order_index: index
      }));
      
      await updateDocumentationOrder(orderData);
      fetchPosts();
    } catch (err) {
      console.error('Error saving order:', err);
      setError('Failed to save order');
    }
  };

  // Filter posts based on search term
  const filteredPosts = posts.filter(post => {
    const lowerCaseSearch = searchTerm.toLowerCase();
    return (
      post.title.toLowerCase().includes(lowerCaseSearch) ||
      post.content.toLowerCase().includes(lowerCaseSearch) ||
      post.roles.some(role => role.name.toLowerCase().includes(lowerCaseSearch))
    );
  });

  // Paginate posts
  const paginatedPosts = filteredPosts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate page count
  const pageCount = Math.ceil(filteredPosts.length / rowsPerPage);

  if (loading && posts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Documentation Management</h1>
        </div>
        <div className="flex justify-center py-16">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Documentation Management</h1>
        <button
          className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors flex items-center"
          onClick={handleCreatePost}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Create Documentation
        </button>
      </div>

      {error && (
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          {error}
        </div>
      )}

      {orderChanged && (
        <DocumentationCard title="" className="bg-gradient-to-r from-status-warning-light-bg to-status-warning-light-bg/80 dark:from-status-warning-dark-bg/30 dark:to-status-warning-dark-bg/30 border border-status-warning-light-bg dark:border-status-warning-dark-bg/50">
          <div className="flex justify-between items-center">
            <span className="text-status-warning-light-text dark:text-status-warning-dark-text">You've changed the order of documentation posts. Click Save Order to apply changes.</span>
            <button
              className="px-4 py-2 text-sm font-medium text-status-warning-light-text dark:text-status-warning-dark-text bg-status-warning-light-bg/30 dark:bg-status-warning-dark-bg/30 rounded-md hover:bg-status-warning-light-bg/50 dark:hover:bg-status-warning-dark-bg/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-status-warning-light-bg dark:focus:ring-offset-ui-dark-background transition-colors"
              onClick={saveOrder}
            >
              Save Order
            </button>
          </div>
        </DocumentationCard>
      )}

      <DocumentationCard title="Documentation Posts">
        <div className="mb-4 relative">
          <input
            type="text"
            placeholder="Search documentation posts..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full p-2 pl-9 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
          />
          <svg
            className="w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary absolute left-2 top-2.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>

        <div className="overflow-x-auto rounded-lg border border-ui-light-border dark:border-ui-dark-border">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-background dark:bg-ui-dark-background">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider w-16">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Published
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {paginatedPosts.length > 0 ? (
                paginatedPosts.map((post, index) => {
                  const actualIndex = posts.findIndex(p => p.id === post.id);
                  return (
                    <tr key={post.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <button
                            className={`p-1 rounded-md ${actualIndex === 0 ? 'text-ui-light-text-muted dark:text-ui-dark-text-muted cursor-not-allowed' : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'}`}
                            onClick={() => handleMoveUp(actualIndex)}
                            disabled={actualIndex === 0}
                            title="Move Up"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                            </svg>
                          </button>
                          <button
                            className={`p-1 rounded-md ${actualIndex === posts.length - 1 ? 'text-ui-light-text-muted dark:text-ui-dark-text-muted cursor-not-allowed' : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'}`}
                            onClick={() => handleMoveDown(actualIndex)}
                            disabled={actualIndex === posts.length - 1}
                            title="Move Down"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {post.title}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                        {format(new Date(post.published_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {post.roles.map(role => (
                            <span
                              key={role.id}
                              className="px-2 py-1 text-xs rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400"
                            >
                              {role.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          className="p-1 text-primary-600 dark:text-primary-400 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-md mr-1"
                          onClick={() => handleEditPost(post)}
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                        </button>
                        <button
                          className="p-1 text-status-error-light-text dark:text-status-error-dark-text hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-md"
                          onClick={() => handleDeletePost(post)}
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center">
                    {loading ? (
                      <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : searchTerm ? (
                      <div className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">No documentation posts match your search.</div>
                    ) : (
                      <div className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">No documentation posts available.</div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {paginatedPosts.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              <span className="text-sm">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                className="bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md p-1 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {[5, 10, 25, 50].map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-sm">
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredPosts.length)} of {filteredPosts.length}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 0}
                className={`p-1 rounded-md ${page === 0 ? 'text-ui-light-text-muted dark:text-ui-dark-text-muted cursor-not-allowed' : 'text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              {Array.from({ length: pageCount }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handleChangePage(i)}
                  className={`w-8 h-8 rounded-md text-sm ${page === i 
                    ? 'bg-primary-600 dark:bg-primary-700 text-white dark:text-ui-dark-text-primary' 
                    : 'text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'}`}
                >
                  {i + 1}
                </button>
              )).slice(Math.max(0, page - 2), Math.min(pageCount, page + 3))}
              <button
                onClick={() => handleChangePage(page + 1)}
                disabled={page >= pageCount - 1}
                className={`p-1 rounded-md ${page >= pageCount - 1 ? 'text-ui-light-text-muted dark:text-ui-dark-text-muted cursor-not-allowed' : 'text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
      </DocumentationCard>

      {/* Quick links */}
      <DocumentationCard title="Documentation Actions">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <button
            onClick={handleCreatePost}
            className="flex flex-col items-center p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Create New</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">Add new documentation entry</span>
          </button>
          
          <button
            onClick={saveOrder}
            disabled={!orderChanged}
            className={`flex flex-col items-center p-4 text-center border ${orderChanged 
              ? 'border-status-warning-light-bg dark:border-status-warning-dark-bg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-status-warning-light-bg dark:hover:border-status-warning-dark-bg/70' 
              : 'border-ui-light-border dark:border-ui-dark-border opacity-60 cursor-not-allowed'} rounded-lg transition-colors`}
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Save Order</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">Persist documentation ordering</span>
          </button>
          
          <button
            onClick={() => setSearchTerm('')}
            className="flex flex-col items-center p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Reset View</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">Clear search and filters</span>
          </button>
        </div>
      </DocumentationCard>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
              Confirm Deletion
            </h3>
            <div className="mb-6 text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Are you sure you want to delete the documentation post "{selectedPost?.title}"? 
              This action cannot be undone.
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePost}
                className="px-4 py-2 text-sm font-medium text-white bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assuming DocumentationPostDialog is adapted elsewhere */}
      {dialogOpen && (
        <DocumentationPostDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          post={selectedPost}
        />
      )}
    </div>
  );
};

export default DocumentationManagement;